import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Sparkles } from "lucide-react";
import { SITE } from "@/lib/site";

const AppleIcon = () => (
  <svg width="14" height="16" viewBox="0 0 24 28" fill="currentColor" aria-hidden>
    <path d="M17.05 14.84c-.03-3.15 2.57-4.66 2.69-4.74-1.46-2.13-3.74-2.42-4.55-2.46-1.94-.2-3.78 1.14-4.77 1.14-.99 0-2.5-1.11-4.11-1.08-2.12.03-4.08 1.23-5.17 3.12-2.2 3.82-.56 9.46 1.59 12.55 1.05 1.51 2.3 3.21 3.94 3.15 1.58-.06 2.18-1.02 4.09-1.02 1.91 0 2.45 1.02 4.13.99 1.71-.03 2.79-1.54 3.83-3.07 1.21-1.76 1.71-3.47 1.74-3.55-.04-.02-3.34-1.28-3.37-5.07Zm-3.13-9.31c.87-1.06 1.46-2.53 1.3-4-1.25.05-2.78.83-3.68 1.89-.8.94-1.51 2.45-1.32 3.89 1.4.11 2.83-.71 3.7-1.78Z" />
  </svg>
);

const PlayIcon = () => (
  <svg width="14" height="16" viewBox="0 0 24 27" fill="currentColor" aria-hidden>
    <path d="M3 1.4v24.2c0 .6.6 1 1.1.7L21 16.6c.8-.5.8-1.7 0-2.2L4.1.7C3.6.4 3 .8 3 1.4Z" />
  </svg>
);

function StoreLink({ href, label, store, testId, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} Safe to Spend 365 on ${store}`}
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A1128] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-[#E6FAF4] active:scale-95 transition-[background-color,transform]"
    >
      {children}
      <span className="hidden sm:inline">{store}</span>
    </a>
  );
}

export default function TrialBanner() {
  const { t } = useTranslation("products");
  const [open, setOpen] = useState(() => sessionStorage.getItem("sts-trial-bar") !== "closed");
  if (!open) return null;
  const sts = SITE.products.safeToSpend;
  const close = () => {
    sessionStorage.setItem("sts-trial-bar", "closed");
    setOpen(false);
  };
  return (
    <div
      data-testid="sts-trial-banner"
      className="sticky top-16 z-40 bg-[#0A1128] text-white border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 min-h-12 py-2 flex items-center gap-3">
        <Sparkles className="hidden sm:block h-4 w-4 text-[#00C48C] shrink-0" />
        <p data-testid="sts-trial-banner-text" className="text-[13px] sm:text-[14px] font-medium leading-snug min-w-0">
          {t("sts.pricing.banner.before")}
          <span className="text-[#00C48C]">{t("sts.pricing.banner.highlight")}</span>
          {t("sts.pricing.banner.after")}
          <span className="hidden lg:inline text-slate-300 font-normal">{t("sts.pricing.banner.cancel")}</span>
        </p>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <StoreLink href={sts.appStoreUrl} label="Download" store="App Store" testId="sts-trial-banner-app-store">
            <AppleIcon />
          </StoreLink>
          <StoreLink href={sts.playStoreUrl} label="Get" store="Google Play" testId="sts-trial-banner-google-play">
            <PlayIcon />
          </StoreLink>
          <button
            type="button"
            onClick={close}
            aria-label={t("sts.pricing.banner.dismiss")}
            data-testid="sts-trial-banner-close"
            className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
